/**
 * UnrespondedLeadAlert — team-wide blinking + audible urgent alert for
 * new leads that haven't been responded to (Ryland's requirement,
 * Jun 11 2026).
 *
 * Renders at the top of My Day. Team-wide + leadership-visible by design:
 * the backend (crm:unresponded-leads) is unscoped, so every CRM user —
 * PEMs, sales, the CEO — sees the same unhandled-lead alert. Pulls fresh
 * every 60s.
 *
 * Behavior (Daniel's 2026-06-11 decisions):
 *   - Escalation tiers 1h → 4h → 24h (warn / urgent / critical), computed
 *     server-side in routes/_unresponded_alert.js.
 *   - Persistent visual: the banner BLINKS (CSS) while any lead is
 *     alerting; faster + redder at higher tiers. Never auto-hides.
 *   - Audible: a soft Web Audio chime on first appearance and on each
 *     tier escalation; at the 'critical' tier it repeats every 30s until
 *     the queue clears. Per-user mutable (toggle persisted in
 *     localStorage); muting silences audio but the blink stays.
 *   - Clicking a lead opens the contact so the PEM can respond; the lead
 *     drops off the next refresh once stage advances or a touch is logged.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { AlertTriangle, Volume2, VolumeX, ChevronDown, ChevronUp } from "lucide-react";

const POLL_MS = 60 * 1000;
const CRITICAL_REPEAT_MS = 30 * 1000;
const MUTE_KEY = "zignagenix.unrespondedAlert.muted";

const TIER_STYLE = {
  warn:     { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E", dot: "#F59E0B", anim: "ur-blink-slow" },
  urgent:   { bg: "#FEF2F2", border: "#DC2626", text: "#991B1B", dot: "#DC2626", anim: "ur-blink" },
  critical: { bg: "#FEE2E2", border: "#991B1B", text: "#7F1D1D", dot: "#991B1B", anim: "ur-blink-fast" },
};

// Lazily-created shared AudioContext + a short two-tone chime. No asset
// files — synthesized so it works offline and adds no bundle weight.
let _audioCtx = null;
function playChime(tier) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const freqs = tier === "critical" ? [880, 1175, 880] : tier === "urgent" ? [740, 988] : [620];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      const t0 = now + i * 0.18;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.18);
    });
  } catch (_) { /* audio unavailable — visual blink still carries the alert */ }
}

export default function UnrespondedLeadAlert({ onOpenContact }) {
  const [state, setState] = useState({ leads: [], total: 0, highestTier: "none", counts: {} });
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === "1"; } catch (_) { return false; }
  });
  const [expanded, setExpanded] = useState(true);
  const prevSig = useRef("");        // signature of last state to detect escalations
  const criticalTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await window.zignagenixAPI.getUnrespondedLeads();
      if (r && !r.error) {
        setState({
          leads: Array.isArray(r.leads) ? r.leads : [],
          total: r.total || 0,
          highestTier: r.highestTier || "none",
          counts: r.counts || {},
        });
      }
    } catch (_) { /* keep last state on transient errors */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Chime on first appearance + on any escalation (new lead, or a lead
  // crossing into a higher tier). Signature captures the per-tier counts.
  useEffect(() => {
    const sig = `${state.counts.warn || 0}-${state.counts.urgent || 0}-${state.counts.critical || 0}`;
    const prev = prevSig.current;
    if (state.total > 0 && sig !== prev && !muted) {
      // Only chime when things got worse (more leads, or higher tier), not
      // when the queue shrinks because someone responded.
      const [pw, pu, pc] = (prev || "0-0-0").split("-").map(Number);
      const worse = (state.counts.critical || 0) > (pc || 0)
        || (state.counts.urgent || 0) > (pu || 0)
        || (state.counts.warn || 0) > (pw || 0)
        || prev === "";
      if (worse) playChime(state.highestTier);
    }
    prevSig.current = sig;
  }, [state, muted]);

  // Critical tier: repeat the chime every 30s until the queue clears.
  useEffect(() => {
    if (criticalTimer.current) { clearInterval(criticalTimer.current); criticalTimer.current = null; }
    if (state.highestTier === "critical" && !muted) {
      criticalTimer.current = setInterval(() => playChime("critical"), CRITICAL_REPEAT_MS);
    }
    return () => { if (criticalTimer.current) clearInterval(criticalTimer.current); };
  }, [state.highestTier, muted]);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch (_) {}
      return next;
    });
  };

  if (state.total === 0) return null;
  const s = TIER_STYLE[state.highestTier] || TIER_STYLE.warn;

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        marginBottom: 16, borderRadius: 12, border: `2px solid ${s.border}`,
        background: s.bg, overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
        <span
          aria-hidden="true"
          style={{
            width: 14, height: 14, borderRadius: 999, background: s.dot, flexShrink: 0,
            animation: `${s.anim} 1s steps(1, end) infinite`,
          }}
        />
        <AlertTriangle size={17} color={s.border} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: s.text }}>
            {state.total} new {state.total === 1 ? "lead has" : "leads have"} not been responded to
          </div>
          <div style={{ fontSize: 11, color: s.text, opacity: 0.85 }}>
            {[
              state.counts.critical ? `${state.counts.critical} critical (24h+)` : null,
              state.counts.urgent ? `${state.counts.urgent} urgent (4h+)` : null,
              state.counts.warn ? `${state.counts.warn} waiting (1h+)` : null,
            ].filter(Boolean).join(" · ")}
            {" — respond now or move the lead to clear this alert."}
          </div>
        </div>
        <button
          onClick={toggleMute}
          title={muted ? "Unmute alert sound" : "Mute alert sound (for you)"}
          aria-label={muted ? "Unmute alert sound" : "Mute alert sound"}
          style={iconBtn(s.border)}
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <button
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? "Collapse list" : "Expand list"}
          style={iconBtn(s.border)}
        >
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${s.border}33`, background: "#fff", maxHeight: 240, overflowY: "auto" }}>
          {state.leads.map((l) => {
            const ts = TIER_STYLE[l.tier] || TIER_STYLE.warn;
            return (
              <button
                key={l.id}
                onClick={() => onOpenContact && onOpenContact(l.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "8px 14px", border: "none", borderBottom: "1px solid #F1F5F9",
                  background: "#fff", cursor: "pointer", textAlign: "left",
                }}
              >
                <span style={{
                  fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em",
                  color: "#fff", background: ts.dot, borderRadius: 999, padding: "2px 8px", flexShrink: 0,
                }}>
                  {l.tier}
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: "#1F2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {l.name}
                  {l.assignee_name
                    ? <span style={{ fontWeight: 400, color: "#6B7280" }}> · {l.assignee_name}</span>
                    : <span style={{ fontWeight: 700, color: "#B45309" }}> · UNASSIGNED</span>}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: ts.dot, flexShrink: 0 }}>
                  waiting {l.elapsed_label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const iconBtn = (color) => ({
  width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center",
  background: "#fff", border: `1px solid ${color}55`, borderRadius: 8, color, cursor: "pointer", flexShrink: 0,
});
